import { mdiCheck, mdiContentCopy } from '@mdi/js';
import Icon from '@mdi/react';
import React from 'react';
import * as S from './payer-id-chip.styles';

/**
 * The ENTER payer ID, ready to paste.
 *
 * Ken Coleta, 2026-08-30: *"We need these IDs when setting up the Chargemaster and when
 * investigating affiliated payers associated with claims."* Both of those mean pasting a GUID
 * into something else, so the useful affordance is COPY, not display — a 36-character UUID that
 * has to be selected by hand is barely better than not having it.
 *
 * Labelled `ENTER ID` and set in monospace on purpose. The same screens show `EDI 36273` a few
 * inches away, and two long identifiers side by side is exactly the confusion that started this:
 * the request was first read as being about EDI numbers, and @John Russell had to correct it —
 * *"Hes talking about the enter payer id. The GUID. It's always there."*
 *
 * Shortened for display, copied in full. What lands on the clipboard is never what is on screen.
 */
export const PayerIdChip: React.FunctionComponent<{ id?: string }> = ({ id }) => {
    const [copied, setCopied] = React.useState(false);
    const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Clear on unmount: the drawer closes while the confirmation is still showing, and a
    // setState on an unmounted component is a warning nobody will chase down later.
    React.useEffect(() => () => timer.current && clearTimeout(timer.current), []);

    if (!id) {
        return null;
    }

    const short = id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;

    const copy = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(id);
            setCopied(true);
            timer.current = setTimeout(() => setCopied(false), 1600);
        } catch {
            // Clipboard is permission-gated and unavailable over plain http on some hosts.
            // The full id is in the title attribute either way, so a failure degrades to
            // select-and-copy rather than to nothing.
        }
    };

    return (
        <S.Chip
            type="button"
            copied={copied}
            title={`ENTER payer ID ${id} — click to copy`}
            aria-label={`Copy ENTER payer ID ${id}`}
            onClick={(e: React.MouseEvent): void => {
                // The row underneath toggles expansion, and the drawer sits over a table.
                e.stopPropagation();
                void copy();
            }}
        >
            <S.Label>ENTER ID</S.Label>
            <S.Value>{copied ? 'Copied' : short}</S.Value>
            <Icon path={copied ? mdiCheck : mdiContentCopy} size={0.5} />
        </S.Chip>
    );
};
